import { isInIframe } from '@shared/utils/isInIframe';
import { getConfig } from '@config';
import { getLocalStorage, getCookieStorage } from '@shared/core/storage';
import globalWindow from '@shared/core/globals';
import { isPhotoDownloadPath } from '@shared/utils/photoDownloadPath';
import { v4 as uuidv4 } from 'uuid';
import { CONSENT_DENIED, CONSENT_WITHOUT_BANNER, getConsentState, narrowConsent, onConsentReady } from './polarisConsent';
import type { ConsentState } from './polarisConsent.types';
import { declinedIntegrations, enforceConsentMiddleware, type SourceMiddleware } from './segmentConsent';
import { followConsentChanges } from './consentNavigation';

export type SegmentOptions = Record<string, boolean>;

const config = getConfig();
const ADD_GIFT_IFRAME_NAME = 'joy-addgift-iframe';
const SAFE_IFRAME_NAMES = [ADD_GIFT_IFRAME_NAME, 'joy-popupblocked-iframe'];
const SAFE_CONTEXT_NAMES = ['admindashboard'];
const SEGMENT_ANONYMOUS_ID_KEY = 'ajs_anonymous_id';
const JOY_ANONYMOUS_ID_KEY = 'joy_anonymous_id';
// Cookie key for anonymous ID (CookieStorage auto-adds 'joy' prefix, so '_anonymous_id' → 'joy_anonymous_id')
const ANONYMOUS_ID_COOKIE_KEY = '_anonymous_id';
// 1 year expiration in seconds (matching Segment's ajs_anonymous_id cookie lifetime)
const ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * This function manages joy-web's own anonymous id, so that it's stable and always available on app load.
 *
 * The anonymous id is synchronized with the segment SDK and is backwards compatible with existing segment anonymous ids.
 */
function getOrGenerateAnonymousId(): string {
  const localStorage = getLocalStorage();
  let anonymousId = localStorage.getItem(JOY_ANONYMOUS_ID_KEY);

  if (!anonymousId) {
    const segmentAnonymousId = (localStorage.getItem(SEGMENT_ANONYMOUS_ID_KEY) || '').replaceAll('"', '');

    if (segmentAnonymousId && segmentAnonymousId.length === 36) {
      // Use segment anonymous id if it roughly looks like a valid uuid (36 chars)
      anonymousId = segmentAnonymousId;
    } else {
      anonymousId = uuidv4();
    }

    localStorage.setItem(JOY_ANONYMOUS_ID_KEY, anonymousId);
  }

  // Sync anonymous ID to cookie for server-side access during SSR
  // Server needs device_id for Amplitude feature flag evaluation during initial render
  const cookieStorage = getCookieStorage();
  if (cookieStorage) {
    const existingCookie = cookieStorage.getItem(ANONYMOUS_ID_COOKIE_KEY);
    if (!existingCookie || existingCookie !== anonymousId) {
      cookieStorage.setItem(ANONYMOUS_ID_COOKIE_KEY, anonymousId, {
        maxAgeInSeconds: ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS,
      });
    }
  }

  return anonymousId;
}

const _ensureAnalyticsIsDefined = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const analytics = (globalWindow.analytics = (globalWindow as any).analytics || []);
  // If the real analytics.js is already on the page return.
  if (analytics.initialize) {
    return;
  }
  // If the snippet was invoked already show an error.
  if (analytics.invoked) {
    return;
  }

  // Invoked flag, to make sure the snippet
  // is never invoked twice.
  analytics.invoked = true;
  // Mark this stub as the one joy-web installed. initSegment() only calls load() on a stub it
  // owns — a stub set up by another snippet (e.g. the legacy AngularJS admin bundle) loads
  // analytics.js on its own, so we must not call load() on it and inject a duplicate script.
  analytics._joyWebOwned = true;
  // A list of the methods in Analytics.js to stub.
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
  ];
  // Define a factory to create stubs. These are placeholders
  // for methods in Analytics.js so that you never have to wait
  // for it to load to actually record data. The `method` is
  // stored as the first argument, so we can replay the data.
  analytics.factory = function (method: unknown) {
    return function () {
      // eslint-disable-next-line prefer-rest-params
      const args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };
  // For each of our methods, generate a queueing stub.
  for (let t = 0; t < analytics.methods.length; t++) {
    const e = analytics.methods[t];
    analytics[e] = analytics.factory(e);
  }
  analytics.load = (key: string, options: unknown) => {
    /**
     * Allow segment if
     * 1- We are not inside an iframe
     * 2- We are inside a specific iframe.
     */
    const checkAllowSegment = () => {
      // MCP draft preview: never load analytics in the forced-SSR preview render (it may be framed in
      // ChatGPT). This is a DEDICATED check, intentionally decoupled from isInIframe(): isInIframe() is
      // forced false on this path for hydration-consistency (admin affordances / page filtering), so it must
      // NOT be read as "not framed → safe to load Segment". Reading the same `__INITIAL_MCP_FORCE_SSR__`
      // marker the server serialized keeps this aligned with the rest of the preview path.
      if ((globalWindow as typeof globalWindow & { __INITIAL_MCP_FORCE_SSR__?: boolean }).__INITIAL_MCP_FORCE_SSR__ === true) {
        return false;
      }

      const locationURL = new URL(globalWindow.location?.href || '');
      const getContext = locationURL.searchParams.get('ctx');

      if (!isInIframe()) {
        return true;
      } else if (globalWindow.name && SAFE_IFRAME_NAMES.includes(globalWindow.name)) {
        return true;
      } else if (getContext && SAFE_CONTEXT_NAMES.includes(getContext)) {
        return true;
      } else {
        return false;
      }
    };

    if (!checkAllowSegment()) {
      // The preview should not load Segment at all.
      // Resolving early only means that we won't send a request for the Segment library
      // as well as any of the integrations - this will drastically reduce load times.
      //
      // We've already defined the analytics object on the window, so resolving now will not
      // cause any services to fail when invoking an analytics method.
      return;
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${config.segmentCdnUrl}/analytics.js/v1/` + key + '/analytics.min.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode!.insertBefore(script, firstScript);
    // Remove so we don't auto track a pagscript.
    // analytics.page();

    analytics._loadOptions = options;
  };

  analytics._writeKey = config.segmentKey;
  analytics.SNIPPET_VERSION = '4.13.2';

  // Control the analytics anonymous id to ensure we always have access to it on app load
  analytics.setAnonymousId(getOrGenerateAnonymousId());
};

const _withAnalytics = (cb: (analytics: SegmentAnalytics.AnalyticsJS) => void) => {
  if (!globalWindow.analytics) {
    _ensureAnalyticsIsDefined();
  }
  cb(globalWindow.analytics!);
};

/** How long after a tracked event its callback runs, giving analytics.js a moment to send it. */
const TRACK_CALLBACK_DELAY_MS = 250;

/** The latest a tracked event's callback runs while the page is still deciding consent. */
const TRACK_CALLBACK_MAX_DELAY_MS = 1000;

let decidedConsent: ConsentState | null = null;
let consentWaiters: Array<() => void> = [];

/**
 * What a missing Polaris cookie means on this page. The add-gift iframe runs inside retailers'
 * pages, where Safari and Firefox block the withjoy.com cookie, so Polaris can neither ask those
 * visitors nor record a choice for them: there it means statistics on, marketing off.
 */
const missingCookieConsent = (): ConsentState => (isInIframe() && globalWindow.name === ADD_GIFT_IFRAME_NAME ? CONSENT_WITHOUT_BANNER : CONSENT_DENIED);

/**
 * Whether this page is an admin screen framed in a signed-in dashboard. Ad and retargeting tags never
 * run there, whatever the visitor allowed: they would sit beside a host's session.
 */
const isEmbeddedDashboard = (): boolean => isInIframe() && SAFE_CONTEXT_NAMES.includes(new URLSearchParams(globalWindow.location?.search ?? '').get('ctx') ?? '');

/** `consent` as this page may act on it: marketing off inside an embedded dashboard. */
const consentForPage = (consent: ConsentState): ConsentState => (isEmbeddedDashboard() ? { ...consent, marketing: false } : consent);

/** The consent the Polaris cookie records for this page right now. */
const readCookieConsent = (): ConsentState => getConsentState(missingCookieConsent());

/**
 * The consent this page acts on: what it decided when Segment loaded, narrowed by any withdrawal
 * the cookie has recorded since. `null` until consent is decided.
 */
const getActiveConsent = (): ConsentState | null => decidedConsent && narrowConsent(decidedConsent, readCookieConsent());

const decideConsent = (consent: ConsentState) => {
  decidedConsent = consent;
  const waiters = consentWaiters;
  consentWaiters = [];
  waiters.forEach(waiter => waiter());
};

const onceConsentDecided = (callback: () => void) => {
  if (decidedConsent) {
    callback();
  } else {
    consentWaiters.push(callback);
  }
};

/**
 * Runs a tracked event's callback once analytics.js has had a moment to send the event. While
 * consent is undecided, analytics.js has not loaded and the event is only queued, so the moment is
 * counted from the decision, but the callback never waits more than {@link TRACK_CALLBACK_MAX_DELAY_MS}.
 */
const runAfterSending = (callback: () => void) => {
  let ran = false;
  const run = () => {
    if (ran) return;
    ran = true;
    callback();
  };
  const cap = setTimeout(run, TRACK_CALLBACK_MAX_DELAY_MS);
  onceConsentDecided(() =>
    setTimeout(() => {
      clearTimeout(cap);
      run();
    }, TRACK_CALLBACK_DELAY_MS),
  );
};

export const initSegment = () => {
  // Device-mode destinations (e.g. Criteo) report `location.href` on load, with no event sent. This
  // page's URL carries a signed zip URL and it sends no events, so analytics.js is never loaded on it.
  if (isPhotoDownloadPath(globalWindow.location?.pathname ?? '')) {
    // Nothing waits for Polaris here, so the page settles consent from the cookie for code that
    // still asks, such as experiment exposures.
    decideConsent(readCookieConsent());
    return;
  }
  const localStorage = getLocalStorage();
  if (!localStorage.getItem('segmentPlatform')) {
    localStorage.setItem('segmentPlatform', 'web');
  }

  _withAnalytics(analytics => {
    // Only initialize through the stub joy-web installed itself. On legacy admin routes
    // (e.g. /edit/guests) the AngularJS Segment snippet installs its own stub and loads
    // analytics.js. Calling load() on that foreign stub — whether the real library has already
    // arrived (`initialize`) or its script is still in-flight (stub not owned by us) — injects a
    // duplicate Segment script and clobbers its load options. `initialize` and `_joyWebOwned`
    // are runtime-only flags absent from the @types/segment-analytics type, so we widen the
    // shape to read them. `_joyWebOwned` is set in _ensureAnalyticsIsDefined only when joy-web
    // builds the stub, so it distinguishes our stub from a foreign one across both races.
    // `addSourceMiddleware` is a stub method the @types/segment-analytics type also leaves out.
    const stub = analytics as SegmentAnalytics.AnalyticsJS & {
      initialize?: boolean;
      _joyWebOwned?: boolean;
      addSourceMiddleware: (middleware: SourceMiddleware) => void;
    };
    const ownsStub = !stub.initialize && stub._joyWebOwned === true && typeof analytics.load === 'function';

    const pathParts = window.location.pathname.split('/') || [];
    const isAdminRoute = /^\/.*\/edit(?:[\/?].*)?$/.test(window.location.pathname);

    const isCardRoute = pathParts[3] === 'card';

    // Queued now so it is in place before load(); analytics.js registers queued middleware before it
    // replays the events queued while consent is decided.
    if (ownsStub) stub.addSourceMiddleware(enforceConsentMiddleware(() => getActiveConsent() ?? CONSENT_DENIED));

    onConsentReady(allowed => {
      const consent = consentForPage(allowed);
      decideConsent(consent);
      if (!ownsStub) return;

      // Both sides see the page's view of the cookie, or the mask reads as a new marketing grant.
      followConsentChanges(consent, () => consentForPage(readCookieConsent()));
      const loadOptions = {
        integrations: {
          ...declinedIntegrations(consent),
          Chameleon: isAdminRoute && isCardRoute,
          Intercom: isAdminRoute,
          'Segment.io': { apiHost: `${config.segmentApiHost}/v1` },
        },
      };

      analytics.load(config.segmentKey, loadOptions);
    }, missingCookieConsent());
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const track = (event: string, callback?: () => void, properties?: object, options?: SegmentAnalytics.SegmentOpts) => {
  _withAnalytics(analytics => {
    analytics.track?.(event, properties, options);
    if (callback) runAfterSending(callback);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const identify: SegmentAnalytics.AnalyticsJS['identify'] = (...args: any[]) => {
  _withAnalytics(analytics => {
    analytics.identify?.(...args);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const page: SegmentAnalytics.AnalyticsJS['page'] = (...args: any[]) => {
  _withAnalytics(analytics => {
    analytics.page?.(...args);
  });
};

const group = (groupId: string, traits?: object, options?: SegmentAnalytics.SegmentOpts, callback?: () => void) => {
  _withAnalytics(analytics => {
    analytics.group?.(groupId, traits, options, callback);
  });
};

export const segment = {
  sendTrackTelemetry: track,
  sendIdentifyTelemetry: identify,
  sendPageTelemetry: page,
  sendGroupTelemetry: group,
  getAnonymousId: getOrGenerateAnonymousId,
  getActiveConsent,
  readCookieConsent,
};
